import { Box, Icon, Menu, Stack, StackProps, Text, useBreakpointValue, useDisclosure } from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import { NavigationPublic, NavigationsPublic } from "../../client"
import { VscTriangleUp } from "react-icons/vsc"
import { FiChevronDown, FiChevronUp } from "react-icons/fi"

interface NavbarLinksProps extends StackProps {
  navPoints: NavigationsPublic | undefined
  onNavClick?: () => void
}

const moreLinks = [
    { label: "Inserate", link: "/inserate" },
    { label: "Abo", link: "/abo" },
]


export const NavbarLinks = ({ navPoints, onNavClick, ...props }: NavbarLinksProps) => {
  const isMobile = useBreakpointValue({ base: true, md: false })
  const { open, onToggle } = useDisclosure()

  return (
    <Stack
      direction={{ base: "column", md: "row" }}
      gap={{ base: "6", md: "8" }}
      alignItems={{base: "flex-start", md: "center"}}
      {...props}
    >
      <Link
        to="/"
        key="home"
        onClick={isMobile ? onNavClick : undefined}
      >
        <Text
          fontSize={18}
          color={{ base: "main", md: "white" }}
          borderBottom="2px solid transparent"
          _hover={{
            borderBottomColor: { md: "white" },
          }}
          css={{
            ".active &": {
              borderBottomColor: { base: "main", md: "white" }
            }
          }}
        >
          Start
        </Text>
      </Link>

      {navPoints?.data.map((item) => (
        item.children && item.children.length > 0 ? (
          <DropMenuPoint key={item.title} item={item} onNavClick={onNavClick} />
        ) : (
          <Link 
            to={item.path} 
            key={item.title}
            onClick={isMobile ? onNavClick : undefined}
          >
            <Text
              fontSize={18}
              color={{ base: "main", md: "white" }}
              borderBottom="2px solid transparent"
              _hover={{
                borderBottomColor: { md: "white" },
              }}
              css={{
                ".active &": {
                  borderBottomColor: { base: "main", md: "white" }
                }
              }}
            >
              {item.title}
            </Text>
          </Link>
        )
      ))}

      {isMobile && 
        <Box>
          <Text
            fontWeight="medium"
            color="main"
            cursor="pointer"
            onClick={onToggle}
            fontSize={18}
          >
            Mehr
            <Icon as={open ? FiChevronUp : FiChevronDown} />
        </Text>

        {open && (
          <Stack gap="6" pl={6} pt={6}>
            {moreLinks.map((link) => (
              <Link key={link.label} to={link.link} onClick={isMobile ? onNavClick : undefined}>
                <Text
                  fontSize={18}
                  color="main"
                  borderBottom="2px solid transparent"
                  css={{
                    ".active &": {
                      borderBottomColor: { base: "main", md: "white" }
                    }
                  }}
                >
                  {link.label}
                </Text>
              </Link>
            ))}
          </Stack>
        )}
      </Box>
      }
    </Stack>
  )
}

const DropMenuPoint = ({ item, onNavClick }: { item: NavigationPublic; onNavClick?: () => void }) => {
  const { open, onOpen, onClose, onToggle } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, md: false })

  if (isMobile) {
    return (
      <Box>
        <Text
          fontWeight="medium"
          color="main"
          cursor="pointer"
          onClick={onToggle}
          fontSize={18}
        >
          {item.title}
          <Icon as={open ? FiChevronUp : FiChevronDown} />
        </Text>

        {open && (
          <Stack gap="6" pl={6} pt={6}>
            {item.children?.map((child) => (
              <Link key={child.title} to={child.path} onClick={isMobile ? onNavClick : undefined}>
                <Text
                  fontSize={18}
                  color="main"
                  borderBottom="2px solid transparent"
                  css={{
                    ".active &": {
                      borderBottomColor: { base: "main", md: "white" }
                    }
                  }}
                >
                  {child.title}
                </Text>
              </Link>
            ))}
          </Stack>
        )}
      </Box>
    )
  }

  return (
    <Box onMouseEnter={onOpen} onMouseLeave={onClose}>
      <Menu.Root open={open} positioning={{placement: "bottom", offset: {mainAxis: -8}}}>
        <Menu.Trigger
          fontWeight="medium"
          color={{ base: "main", md: "white" }}
          w="fit-content"
          asChild
          pb="2px"
        >
          <Text
            fontSize={18}
            color={{ base: "main", md: "white" }}
          >
            {item.title}
            <Icon as={open ? FiChevronUp : FiChevronDown} />
          </Text>
        </Menu.Trigger>

        <Menu.Positioner textAlign="center">
          <Icon
            as={VscTriangleUp}
            zIndex={1000}
            fill="white"
            color="white"
            verticalAlign="middle"
            size="xl"
            viewBox="0 0 16 10"
          />
          <Menu.Content bgColor="main" borderRadius={0}>
            {item.children?.map((child) => (
              <Menu.Item
                key={child.title}
                value={child.title}
                asChild
                cursor="pointer"
              >
                <Link to={child.path}>
                  <Text
                    fontSize={18}
                    color={{ base: "main", md: "white" }}
                    css={{
                      ".active &": {
                        borderBottomColor: "white"
                      }
                    }}
                  >
                    {child.title}
                  </Text>
                </Link>
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>
    </Box>
  )
}